// AI Agent effectiveness scorecard — rows = bots, columns capture their
// behaviour across the window. Supervisor glance → "which bots are
// earning their keep, which need tuning?".
//
// Pure-client aggregation over contactAIAgentSession rows grouped by
// agentName. All metrics come from fields already on the row.

import React from 'react';
import { Badge, Progress, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';
import { fmtDuration } from '../utils';

const REPORT_ID = 'bot-effectiveness';

const COLUMNS = [
  {
    id: 'name',
    label: 'Bot',
    pinned: true,
    render: (r) => (
      <div>
        <Text size="sm" fw={500}>{r.name || <em>(unnamed)</em>}</Text>
        {r.types.length > 0 && (
          <Text size="xs" c="dimmed">{r.types.join(' / ')}</Text>
        )}
      </div>
    ),
  },
  {
    id: 'sessions',
    label: 'Sessions',
    align: 'right',
    render: (r) => <Text size="sm">{r.sessions}</Text>,
  },
  {
    id: 'uniqueCallers',
    label: 'Unique callers',
    hint: 'Distinct ANIs the bot talked to in the window.',
    align: 'right',
    render: (r) => <Text size="sm">{r.uniqueCallers}</Text>,
  },
  {
    id: 'containment',
    label: 'Containment',
    hint: 'Share of sessions whose outcome is HANDLED/COMPLETED. Higher = fewer human handoffs.',
    align: 'right',
    render: (r) => <RateBadge pct={r.containmentPct} color="teal" />,
  },
  {
    id: 'escalation',
    label: 'Escalation',
    hint: 'Share handed off to a human.',
    align: 'right',
    render: (r) => <RateBadge pct={r.escalationPct} color="blue" />,
  },
  {
    id: 'abandonment',
    label: 'Abandonment',
    hint: 'Share where the caller gave up mid-conversation.',
    align: 'right',
    render: (r) => <RateBadge pct={r.abandonmentPct} color="orange" />,
  },
  {
    id: 'failRate',
    label: 'Failed',
    hint: 'Share of sessions with outcome FAILED.',
    align: 'right',
    render: (r) => <RateBadge pct={r.failPct} color="red" />,
  },
  {
    id: 'evalScore',
    label: 'Avg eval',
    hint: 'Mean overallEvalScore (0–1). Webex\'s own quality rubric.',
    align: 'right',
    render: (r) => <EvalBadge score={r.avgEvalScore} />,
  },
  {
    id: 'avgMessages',
    label: 'Avg msgs',
    hint: 'Mean messages per session. Very short = hangups; very long = stuck.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgMessages?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'avgResponseTime',
    label: 'Avg response',
    hint: 'Mean averageResponseTime across sessions. Latency ↑ = user frustration ↑.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgResponseMs != null ? `${(r.avgResponseMs / 1000).toFixed(1)}s` : '—'}</Text>,
  },
  {
    id: 'avgDuration',
    label: 'Avg duration',
    hint: 'Mean session length.',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgDurationMs)}</Text>,
  },
  {
    id: 'deadAir',
    label: 'Avg dead air',
    hint: 'Mean deadAirCount per session. Spikes suggest slow backing LLM or fulfillment URL.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgDeadAir?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'bargeIn',
    label: 'Avg barge-in',
    hint: 'Mean bargeInCount per session. Spikes suggest TTS turns are too long or caller is impatient.',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgBargeIn?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'topIntent',
    label: 'Top intent',
    hint: 'Most common intent string in the window.',
    render: (r) => <Text size="xs">{r.topIntent || '—'}</Text>,
  },
  {
    id: 'topEntryPoint',
    label: 'Top entry',
    render: (r) => <Text size="xs">{r.topEntryPoint || '—'}</Text>,
  },
  {
    id: 'mix',
    label: 'Outcome mix',
    hint: 'Visual outcome split: handled, escalated, abandoned, failed.',
    width: 180,
    render: (r) => (
      <Tooltip label={`${r.containmentPct}% handled · ${r.escalationPct}% escalated · ${r.abandonmentPct}% abandoned · ${r.failPct}% failed`}>
        <Progress.Root size="md">
          {r.containmentPct > 0 && <Progress.Section value={r.containmentPct} color="teal" />}
          {r.escalationPct > 0 && <Progress.Section value={r.escalationPct} color="blue" />}
          {r.abandonmentPct > 0 && <Progress.Section value={r.abandonmentPct} color="orange" />}
          {r.failPct > 0 && <Progress.Section value={r.failPct} color="red" />}
        </Progress.Root>
      </Tooltip>
    ),
  },
];

const DEFAULT_VISIBLE = ['sessions', 'uniqueCallers', 'containment', 'escalation', 'abandonment', 'evalScore', 'avgResponseTime', 'mix'];

export function BotEffectiveness({ data }) {
  const rows = buildRows(data.sessions);
  return (
    <ReportTable
      title="AI Agent effectiveness scorecard"
      subtitle={`${rows.length} bot${rows.length === 1 ? '' : 's'} active in window · based on contactAIAgentSession`}
      reportId={REPORT_ID}
      columns={COLUMNS}
      defaultVisibleIds={DEFAULT_VISIBLE}
      rows={rows}
      emptyLabel="No AI Agent sessions recorded in this window."
    />
  );
}

function buildRows(sessions) {
  const buckets = new Map();
  for (const s of sessions) {
    const name = s.agentName ?? null;
    if (!name) continue;
    if (!buckets.has(name)) {
      buckets.set(name, {
        key: name,
        name,
        sessions: 0,
        callers: new Set(),
        types: new Set(),
        intents: new Map(),
        entries: new Map(),
        handled: 0, escalated: 0, abandoned: 0, failed: 0, other: 0,
        sumEval: 0, countEval: 0,
        sumMsgs: 0, countMsgs: 0,
        sumResp: 0, countResp: 0,
        sumDur: 0, countDur: 0,
        sumDead: 0, countDead: 0,
        sumBarge: 0, countBarge: 0,
      });
    }
    const b = buckets.get(name);
    b.sessions += 1;
    if (s.ani) b.callers.add(s.ani);
    if (s.agentType) b.types.add(s.agentType);
    bump(b.intents, s.intent);
    bump(b.entries, s.entryPoint?.name);
    switch (s.interactionOutcome) {
      case 'HANDLED': case 'COMPLETED': b.handled += 1; break;
      case 'ESCALATED': b.escalated += 1; break;
      case 'ABANDONED': b.abandoned += 1; break;
      case 'FAILED': b.failed += 1; break;
      default: b.other += 1;
    }
    if (s.overallEvalScore != null) { b.sumEval += s.overallEvalScore; b.countEval += 1; }
    if (s.noOfTotalMessages != null) { b.sumMsgs += s.noOfTotalMessages; b.countMsgs += 1; }
    if (s.averageResponseTime != null) { b.sumResp += s.averageResponseTime; b.countResp += 1; }
    if (s.duration != null) { b.sumDur += s.duration; b.countDur += 1; }
    if (s.deadAirCount != null) { b.sumDead += s.deadAirCount; b.countDead += 1; }
    if (s.bargeInCount != null) { b.sumBarge += s.bargeInCount; b.countBarge += 1; }
  }

  return [...buckets.values()]
    .map((b) => ({
      key: b.key,
      name: b.name,
      sessions: b.sessions,
      uniqueCallers: b.callers.size,
      types: [...b.types],
      containmentPct: pct(b.handled, b.sessions),
      escalationPct: pct(b.escalated, b.sessions),
      abandonmentPct: pct(b.abandoned, b.sessions),
      failPct: pct(b.failed, b.sessions),
      avgEvalScore: safeAvgFloat(b.sumEval, b.countEval),
      avgMessages: safeAvgFloat(b.sumMsgs, b.countMsgs),
      avgResponseMs: safeAvgInt(b.sumResp, b.countResp),
      avgDurationMs: safeAvgInt(b.sumDur, b.countDur),
      avgDeadAir: safeAvgFloat(b.sumDead, b.countDead),
      avgBargeIn: safeAvgFloat(b.sumBarge, b.countBarge),
      topIntent: topKey(b.intents),
      topEntryPoint: topKey(b.entries),
    }))
    .sort((a, b) => b.sessions - a.sessions);
}

function bump(map, key) {
  if (!key) return;
  map.set(key, (map.get(key) ?? 0) + 1);
}
function topKey(map) {
  let best = null, bestN = 0;
  for (const [k, n] of map) if (n > bestN) { best = k; bestN = n; }
  return best;
}
function safeAvgFloat(sum, count) { return count > 0 ? sum / count : null; }
function safeAvgInt(sum, count) { return count > 0 ? Math.round(sum / count) : null; }
function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }

function RateBadge({ pct, color }) {
  if (pct == null) return <Text size="xs" c="dimmed">—</Text>;
  const variant = pct >= 20 ? 'filled' : 'light';
  return <Badge size="sm" color={color} variant={variant}>{pct}%</Badge>;
}

function EvalBadge({ score }) {
  if (score == null) return <Text size="xs" c="dimmed">—</Text>;
  const color = score < 0.5 ? 'red' : score < 0.7 ? 'orange' : score < 0.85 ? 'yellow' : 'teal';
  return <Badge size="sm" color={color} variant="light">{score.toFixed(2)}</Badge>;
}
